import { ReactElement, useCallback, useState } from 'react';

export function useMultistepForm(
  formName: string,
  steps: ReactElement[],
  hasCompletionStep = false,
  saveToLocalStorage = true,
) {
  const form = `${formName}-currentStep`;
  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    const storedIndex = localStorage.getItem(`${form}`);
    return storedIndex ? parseInt(storedIndex, 10) : 0;
  });

  const saveStepToLocalStorage = useCallback(
    (index: number) => {
      if (saveToLocalStorage) {
        localStorage.setItem(form, index.toString());
      }
    },
    [form, saveToLocalStorage],
  );

  const removeStepFromLocalStorage = () => {
    if (saveToLocalStorage) {
      localStorage.removeItem(form);
    }
  };

  let stepNames = steps.map((step, index) => (step.props?.name ? step.props.name : `Step ${index + 1} missing name`));
  if (hasCompletionStep) {
    stepNames = stepNames.slice(0, stepNames.length - 1);
  }

  const next = () => {
    setCurrentStepIndex(i => {
      const nextIndex = i + 1;
      if (nextIndex < steps.length) {
        saveStepToLocalStorage(nextIndex);
        return nextIndex;
      }
      return i;
    });
  };

  const back = () => {
    setCurrentStepIndex(i => {
      const prevIndex = i - 1;
      if (prevIndex >= 0) {
        saveStepToLocalStorage(prevIndex);
        return prevIndex;
      }
      return i;
    });
  };

  const goTo = (index: number) => {
    if (index >= 0 && index < steps.length) {
      setCurrentStepIndex(index);
      saveStepToLocalStorage(index);
    }
  };

  const completeForm = () => {
    removeStepFromLocalStorage();
  };

  const step = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const length = hasCompletionStep ? steps.length - 1 : steps.length;
  return {
    step,
    steps,
    stepNames,
    currentStepStorageKey: form,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    length,
    next,
    back,
    goTo,
    completeForm,
  };
}
